// General
import React, { useState, useContext, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
// UI / MUI
import { TextField, Autocomplete } from "@mui/material";
// Core / Table / Custom Components
import TablePage from "../core/TablePage";
import SoftBlinkingRedLightEmoji from "../core/UrgencyCell";
// Modals
import ServiceAddModal from "../modals/service/ServiceAddModal";
import ServiceUpdateModal from "../modals/service/ServiceUpdateModal";
import QuickAssignModal from "../modals/service/QuickAssignModal";
// Contexts
import { ServiceContext } from "../data/services/ServiceContext";
import { ActionContext } from "../data/action/ActionContext";
import { SubActionContext } from "../data/action/SubActionContext";
import { useData } from "../data/general/DataContext";
// Redux Actions
import { getCities, getDistricts } from "../data/general/LocationSlice";
// PrimeReact
import { Button } from "primereact/button";
import { OverlayPanel } from "primereact/overlaypanel";
// Icons
import { FaTrash, FaPencilAlt } from "react-icons/fa";
import SoftBlinkingRedLightGif from "../core/UrgencyCell";
import redLightGif from '../assets/images/giphy.webp';

const ServicePage = () => {
  const { services, deleteService } = useContext(ServiceContext);
  const { subActions } = useContext(SubActionContext);
  const { windowMode } = useData();
  const dispatch = useDispatch();
  const { cities, districts } = useSelector((state) => state.location);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [searchedCity, setSearchedCity] = useState("");
  const { actions } = useContext(ActionContext);

  const [showQuickAssignModal, setShowQuickAssignModal] = useState(false); 
  const [columnFilters, setColumnFilters] = useState({
    city: null,
    district: null,
    phone3: null,
  });
  // Seçili ana aksiyon ve alt aksiyon adlarını çözer
  const resolveActionInfo = (row) => {
    const assignedAction = actions?.find((a) => a.id === row.actionId);
    const actionName = assignedAction?.name || row.actionName || "Beklemede";

    const sub = row.subActionId
      ? subActions?.find((sa) => sa.id === row.subActionId)
      : null;
    const subActionName = sub?.name || null;

    return { actionName, subActionName };
  };


  // Şehirleri yükle
  useEffect(() => {
    dispatch(getCities());
  }, [dispatch]);

  // İlçeleri yükle
  useEffect(() => {
    if (columnFilters.city) {
      dispatch(getDistricts({ city: columnFilters.city }));
    }
  }, [columnFilters.city, dispatch]);

  // Masaüstü için OverlayPanel menüsü
  const ActionMenu = ({ rowData }) => {
    const op = useRef(null);
    if (windowMode === "mobile") return null;

    return (
      <>
        <Button
          label="..."
          className="p-button-rounded p-button-text"
          style={{ minWidth: "40px", fontSize: "22px", fontWeight: "bold" }}
          onClick={(e) => op.current.toggle(e)}
        />
        <OverlayPanel ref={op} dismissable showCloseIcon={false} style={{ padding: "0.5rem", left: "-5px" }}>
          <div className="flex flex-col gap-0 p-2 w-32">
            <Button
              icon={<FaPencilAlt />}
              label="Düzenle"
              className="p-button-text w-full justify-start"
              style={{ fontSize: "14px", gap: "10px" }}
              onClick={() => {
                setSelectedService(rowData);
                setShowUpdateModal(true);
                op.current.hide();
              }}
            />
            <div className="w-full border-t border-gray-300 my-1"></div>
            <Button
              icon={<FaTrash />}
              label="Sil"
              className="p-button-text w-full justify-start"
              style={{ color: "red", fontSize: "14px", gap: "10px" }}
              onClick={() => {
                deleteService(rowData.id);
                op.current.hide();
              }}
            />
          </div>
        </OverlayPanel>
      </>
    );
  };

  // Mobil görünüm
  const MobileActions = ({ rowData }) => {
    if (windowMode !== "mobile") return null;
    return (
      <div className="flex gap-2 mt-2 w-full">
        <Button
          icon={<FaPencilAlt />}
          label="Düzenle"
          className="flex-1"
          style={{
            backgroundColor: "#3b82f6",
            color: "white",
            border: "none",
            borderRadius: "6px",
            height: "26px",     
            fontSize: "10px",    
            justifyContent: "center",
            gap: "4px",
            padding: "0 6px",    
          }}
          onClick={() => {
            setSelectedService(rowData);
            setShowUpdateModal(true);
          }}
        />
        <Button
          icon={<FaTrash />}
          label="Sil"
          className="flex-1"
          style={{
            backgroundColor: "#ef4444",
            color: "white",
            border: "none",
            borderRadius: "6px",
            height: "26px",     
            fontSize: "10px",   
            justifyContent: "center",
            gap: "4px",
            padding: "0 6px",    
          }}
          onClick={() => deleteService(rowData.id)}
        />

      </div>
    );
  };

  const LocationFilter = () => (
    <div className="flex flex-col gap-2">
      <Autocomplete
        options={cities || []}
        value={columnFilters.city || ""}
        onChange={(e, newValue) => {
          setColumnFilters({ ...columnFilters, city: newValue, district: "" });
          if (newValue && newValue !== searchedCity) {
            dispatch(getDistricts({ city: newValue }));
            setSearchedCity(newValue);
          }
        }}
        getOptionLabel={(option) => option}
        renderOption={(props, option) => (
          <li {...props} key={option} className="p-2 hover:bg-gray-100 cursor-pointer">
            {option}
          </li>
        )}
        renderInput={(params) => <TextField {...params} label="Şehir seçin" size="small" />}
        disablePortal
        autoHighlight
        style={{ width: 200 }}
      />
      <Autocomplete
        options={districts || []}
        value={columnFilters.district || ""}
        onChange={(e, newValue) => setColumnFilters({ ...columnFilters, district: newValue })}
        getOptionLabel={(option) => option}
        renderOption={(props, option) => (
          <li {...props} key={option} className="p-2 hover:bg-gray-100 cursor-pointer">
            {option}
          </li>
        )}
        renderInput={(params) => <TextField {...params} label="İlçe seçin" size="small" />}
        disablePortal
        autoHighlight
        disabled={!columnFilters.city}
        style={{ width: 200 }}
      />
    </div>
  );

  // Tabloda kolonlar
  const columns = [
    {
      field: "id",
      header: "ID",
      hideInFilter: true,
     body: (rowData) => (
  <div className="flex items-center gap-5">
    <span>{rowData.id}</span>
    {rowData.urgency === "Yüksek" && (
      <SoftBlinkingRedLightGif
        size={20} 
        blinkSpeed={100} 
        gifUrl={redLightGif} 
      />
    )}
  </div>
),
      style: { width: "70px" },
    }
    , { field: "customerName", header: "Müşteri", },
    { field: "device", header: "Cihaz" },
    { field: "brand", header: "Marka" },
    { field: "phone3", header: "Kaynak " },
    {
      field: "urgency",
      header: "Aciliyet",
      hideInTable: true
    }
    , { field: "operator", header: "Operatör", hideInTable: true },
    { field: "teknisyen", header: "Teknisyen", hideInTable: true },
    {
      field: "address",
      hideInTable: true,
      header: "Adres",
      body: (rowData) => (
        <div className="flex flex-col leading-tight">
          <span className="text-sm">{rowData.address}</span>
          <span className="text-xs text-gray-500">{rowData.city} / {rowData.district}</span>
        </div>
      ),
    },
    {
      field: "numberOfComplaint",
      header: "Şikayet",
      body: (rowData) => {

        return (
          <span className={` text-black  px-3 py-0  `}>
            {rowData.numberOfComplaint}
          </span>
        );
      },
    },
    {
      field: "status",
      header: "Servis Durumu",
      body: (row) => {
        const { actionName, subActionName } = resolveActionInfo(row);
        const hasExtra = !!row.extraData;
        return (
          <div className="flex flex-col gap-1 leading-tight">
            <span className="font-semibold">{actionName}</span>


          </div>
        );
      },
    },
    {
      header: "İşlemler",
      body: (rowData) =>
        windowMode === "mobile" ? <MobileActions rowData={rowData} /> : <ActionMenu rowData={rowData} />,
      hideInFilter: true,
      style: { width: "120px", textAlign: "center" },
    }
  ];

  // Filtreleme
  const columnFilterOptions = {
    customerName: { type: "text" },
    device: { type: "text" },
    brand: { type: "text" },
    phone3: { type: "text" },
    address: { type: "custom", body: LocationFilter },
    urgency: { type: "checkbox", options: ["Yüksek", "Orta", "Düşük"] },
    status: { type: "checkbox", options: ["Beklemede", "Devam Ediyor", "Tamamlandı"] },
  };
  const filteredServices = services.filter(
    (s) =>
      (!columnFilters.city || s.city === columnFilters.city) &&
      (!columnFilters.district || s.district === columnFilters.district) &&
      (!columnFilters.phone3 || s.phone3?.toLowerCase().includes(columnFilters.phone3.toLowerCase()))
  );

  return (
    <>
      <div className="p-6">
        <TablePage
          title="Servis"
          subTitle="Servis işlemlerini ekleyebilir, güncelleyebilir veya silebilirsiniz."
          columns={columns}
          data={filteredServices}
          defaultRows={10}
          rowsPerPageOptions={[10, 15, 25, 50,100]}
          showSearch
          showDateFilter
          onAdd={() => setShowAddModal(true)}
          columnFilterOptions={columnFilterOptions}
 rowClassName={(rowData) => {
  const { actionName } = resolveActionInfo(rowData);
  const complaint = parseInt(rowData.numberOfComplaint, 10) || 0;

  // Şikayet sayısına göre soft renkler
  const getComplaintColor = (count) => {
    if (count === 1) return "bg-yellow-soft";       
    if (count === 2) return "bg-orange-soft";        
    if (count === 3) return "bg-red-soft text-white";           
    if (count >= 4) return "bg-rose-900 text-white";
    return "";
  };

  // Servisi Sonlandır -> soft gri
  if (actionName === "Servis Sonlandırıldı") {
    return "bg-gray-100";
  }

  // Yeni Servisler -> şikayet varsa şikayet rengi, yoksa soft yeşil
  if (actionName === "Yeni Servis") {
    return complaint > 0 ? getComplaintColor(complaint) : "bg-green-soft";
  }

  // Diğer durumlar -> şikayet varsa şikayet rengi, yoksa soft mavi
  return complaint > 0 ? getComplaintColor(complaint) : "bg-blue-soft";
}}


        />
        {/* Tablo altına buton */}

      </div>

      <ServiceAddModal show={showAddModal} handleClose={() => setShowAddModal(false)} />
      <ServiceUpdateModal
        show={showUpdateModal}
        handleClose={() => setShowUpdateModal(false)}
        service={selectedService}
      />

    </>
  );
};

export default ServicePage;
